import { CHAT_UI_PIPELINE } from './chat-ui.pipeline';
import {
  TChatUiModel,
  TChatUiModelFeedback,
  TChatUiModelOptions,
  TChatUiTableAndChartResponseData,
} from './chat-ui.types';

export const ChatUiModelModifiers = {
  createChatUiWithData: function <T>(
    data: T[],
    options: TChatUiModelOptions,
    createChats: (el: T) => TChatUiModel<T>['chats'],
  ): TChatUiModel<T> {
    return {
      chats: data.flatMap(createChats),
      queryValue: '',
      isWaiting: false,
      isSendBtnDisabled: true,
      options,
    };
  },

  createIdentityModel: function <T>(): TChatUiModel<T> {
    return {
      chats: [],
      queryValue: '',
      isWaiting: false,
      isSendBtnDisabled: true,
      options: {
        showWaitingAfterQuerySend: true,
        hideWaitingAfterResponseReceive: true,
        disableSendBtnWhileWaiting: true,
      },
    };
  },

  updateQueryInModel: function <T>(prevModel: TChatUiModel<T>, query: string): TChatUiModel<T> {
    const updatedModel = {
      ...prevModel,
      queryValue: query,
    };
    return CHAT_UI_PIPELINE.updateQueryPipeline(updatedModel);
  },

  addNewChat: function <T>(prevModel: TChatUiModel<T>, chat: TChatUiModel<T>['chats'][number]): TChatUiModel<T> {
    const updatedModel = {
      ...prevModel,
      chats: [...prevModel.chats, chat],
    };
    if (chat.type === 'QUERY') return CHAT_UI_PIPELINE.addNewQueryPipeline(updatedModel);
    if (chat.type === 'SEPARATOR') return updatedModel;
    return CHAT_UI_PIPELINE.addNewResponsePipeline(updatedModel);
  },

  changeTabInResponseByIndex: function <T>(
    prevModel: TChatUiModel<T>,
    index: number,
    changedTab: TChatUiTableAndChartResponseData['tab'],
  ): TChatUiModel<T> {
    return {
      ...prevModel,
      chats: prevModel.chats.map((chat, i): TChatUiModel<T>['chats'][number] =>
        i === index && chat.type === 'RESPONSE' && chat.responseType === 'TABLE_AND_CHART'
          ? {
              ...chat,
              data: {
                ...chat.data,
                tab: changedTab,
              },
            }
          : chat,
      ),
    };
  },

  submitFeedbackForResponseByIndex: function <T>(
    prevModel: TChatUiModel<T>,
    index: number,
    feedback: TChatUiModelFeedback,
  ): TChatUiModel<T> {
    return {
      ...prevModel,
      chats: prevModel.chats.map((chat, i): TChatUiModel<T>['chats'][number] =>
        i === index &&
        chat.type === 'RESPONSE' &&
        (chat.responseType === 'TABLE_AND_CHART' || chat.responseType === 'TEXT')
          ? { ...chat, feedback }
          : chat,
      ),
    };
  },

  upsertTrailingSeparator: function <T>(prevModel: TChatUiModel<T>, text: string): TChatUiModel<T> {
    const chats = prevModel.chats;
    const lastChat = chats[chats.length - 1];
    if (lastChat?.type === 'SEPARATOR') {
      return { ...prevModel, chats: [...chats.slice(0, -1), { type: 'SEPARATOR' as const, text }] };
    }
    return { ...prevModel, chats: [...chats, { type: 'SEPARATOR' as const, text }] };
  },

  updateInsightsInfoForTableChartResponseByIndex: function <T>(
    prevModel: TChatUiModel<T>,
    index: number,
    insightsInfo: TChatUiTableAndChartResponseData['insightsInfo'],
  ): TChatUiModel<T> {
    return {
      ...prevModel,
      chats: prevModel.chats.map((chat, i): TChatUiModel<T>['chats'][number] =>
        i === index && chat.type === 'RESPONSE' && chat.responseType === 'TABLE_AND_CHART'
          ? { ...chat, data: { ...chat.data, insightsInfo } }
          : chat,
      ),
    };
  },
};
